/**
 * The composer's transactions (F-JR-23, F-JR-24, F-JR-25).
 *
 * Everything with a rule in it lives here rather than in a component, for the
 * same reason the privacy session does: the properties that matter — a debounce
 * that coalesces, a `savedAt` that strictly increases, a promotion that reopens
 * rather than dismisses when a keystroke lands mid-flight — are the ones most
 * worth testing without a renderer, and a rule expressed as a `useEffect` is a
 * rule whose timing depends on whether a test remembered to wrap something in
 * `act`.
 *
 * Three invariants shape the whole file:
 *
 * 1. **One writer.** Autosave never runs two writes concurrently. A second
 *    write racing the first would order by whichever storage call returned
 *    last, and the loser would be the newer text.
 * 2. **`savedAt` strictly increases.** Drafts order by timestamp, not by
 *    revision, so a clock that does not move — or moves backwards across a
 *    system time change — must not let an older autosave win the "newest"
 *    comparison and resurrect text the user already replaced.
 * 3. **Only a completed transaction closes the composer.** Every other outcome
 *    leaves the user's writing on screen. Dismissing on a failed promotion is
 *    exactly how writing gets lost with no way back.
 */

import type {
  JournalAttachmentId,
  JournalRepositoryErrorCode,
  JournalAutosaveState,
  JournalComposerOrigin,
  JournalComposerSeed,
  JournalDraft,
  JournalEntry,
  JournalFinishOutcome,
  JournalInstant,
} from "@ff/domain";
import {
  JOURNAL_AUTOSAVE_DEBOUNCE,
  JOURNAL_DRAFT_SCHEMA_VERSION,
  journalHasUnsavedChanges,
  journalInstantFromMilliseconds,
  nextJournalRevision,
  nextJournalSavedAt,
} from "@ff/domain";
import type {
  AnnouncerPort,
  Cancel,
  ClockPort,
  JournalRepositoryPort,
  TickerPort,
} from "@ff/domain/ports";

import { journalAutosaveAnnouncement, journalAutosaveFailureMessage } from "./autosave";
import type { JournalCloseDecision, JournalDiscardOutcome } from "./close";
import { JOURNAL_DISCARD_FAILURE, journalCloseDecision } from "./close";
import type { ComposerSeedContext, JournalComposerIdentity } from "./seeds";
import {
  composerSeedEntry,
  composerSeedIsDirty,
  composerSeedOrigin,
  composerSeedOriginEntry,
  journalComposerIsBlank,
} from "./seeds";

/** What the surface renders. Replaced wholesale on every change. */
export interface JournalComposerSnapshot {
  readonly entry: JournalEntry;
  readonly origin: JournalComposerOrigin;
  readonly autosave: JournalAutosaveState;
  /** False while a finish transaction is in flight (F-JR-27). */
  readonly isEditingEnabled: boolean;
  readonly isFinishing: boolean;
  readonly hasUnsavedChanges: boolean;
  /** Attachments removed in this session, deleted only on a successful finish. */
  readonly pendingAttachmentDeletions: readonly JournalAttachmentId[];
}

export interface JournalComposerDependencies {
  readonly repository: JournalRepositoryPort;
  readonly clock: ClockPort;
  readonly ticker: TickerPort;
  readonly identity: JournalComposerIdentity;
  /** Absent in a build with no announcer adapter; §8.5 degrades, never throws. */
  readonly announcer?: AnnouncerPort | undefined;
  /**
   * Whether a same-id draft already shadows the origin entry. Part of F-JR-25's
   * matrix, and known only to the surface that opened the composer.
   */
  readonly hasSameIdDraft?: boolean;
  /** F-JR-24 refuses to finish while media is still importing. */
  readonly isMediaImportInProgress?: () => boolean;
  /**
   * Removes the attachments the user detached. Absent means this build cannot
   * detach media at all, in which case nothing ever accumulates to clean up.
   */
  readonly deleteAttachments?: (
    ids: readonly JournalAttachmentId[],
  ) => Promise<boolean>;
  /**
   * Structural equality over two records. Injected because the only canonical
   * encoder in the workspace is `@ff/store`'s, and a controller that imported it
   * would make every test of these rules need a storage package.
   */
  readonly sameRecord: (left: JournalEntry, right: JournalEntry) => boolean;
}

const UNTOUCHED: JournalAutosaveState = Object.freeze({ kind: "untouched" });
const WAITING: JournalAutosaveState = Object.freeze({ kind: "waiting" });
const SAVING: JournalAutosaveState = Object.freeze({ kind: "saving" });

export class JournalComposerController {
  readonly #dependencies: JournalComposerDependencies;
  readonly #seed: JournalComposerSeed;
  readonly #origin: JournalComposerOrigin;
  readonly #originEntry: JournalEntry | undefined;

  #listeners = new Set<() => void>();
  #snapshot: JournalComposerSnapshot;

  /**
   * Bumped by every mutation. It is the only thing that decides whether a write
   * is still wanted, which is what lets the writer loop instead of racing: the
   * loop compares the generation it started from with the current one and runs
   * again when they differ.
   */
  #generation = 0;
  #savedGeneration = 0;
  #lastSavedAt: JournalInstant | null = null;

  #cancelDebounce: Cancel | undefined;
  #writer: Promise<void> | null = null;
  #hasSameIdDraft: boolean;

  /**
   * A promotion that succeeded and whose cleanup did not. Held so the next
   * Done retries **cleanup only** — re-promoting a durable entry would be a
   * second revision step the repository refuses, and the user would be stuck.
   */
  #promoted: JournalEntry | undefined;

  constructor(seed: JournalComposerSeed, dependencies: JournalComposerDependencies) {
    this.#dependencies = dependencies;
    this.#seed = seed;
    this.#origin = composerSeedOrigin(seed);
    this.#originEntry = composerSeedOriginEntry(seed);
    this.#hasSameIdDraft = dependencies.hasSameIdDraft ?? false;

    const context: ComposerSeedContext = {
      now: journalInstantFromMilliseconds(dependencies.clock.now()),
      identity: dependencies.identity,
    };
    const entry = composerSeedEntry(seed, context);
    const dirty = composerSeedIsDirty(seed, entry, dependencies.sameRecord);

    this.#snapshot = {
      entry,
      origin: this.#origin,
      autosave: UNTOUCHED,
      isEditingEnabled: true,
      isFinishing: false,
      hasUnsavedChanges: false,
      pendingAttachmentDeletions: [],
    };

    // A dirty seed schedules its first save immediately rather than waiting for
    // a keystroke: a recap's effort embed and back-dated moment are content the
    // user never typed, and a composer that opened `untouched` would preserve
    // none of it if they closed without writing.
    if (dirty) this.#touch();
  }

  /* --- subscription ------------------------------------------------------ */

  subscribe = (listener: () => void): Cancel => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  snapshot = (): JournalComposerSnapshot => this.#snapshot;

  /* --- mutation ---------------------------------------------------------- */

  /**
   * The only way the working entry changes.
   *
   * Every mutation bumps the change version, sets `.waiting` and schedules the
   * 650 ms debounced write. Routing all of them through one method is what makes
   * "every mutation is autosaved" a property of the type rather than a habit.
   */
  mutate(change: (entry: JournalEntry) => JournalEntry): void {
    // Deliberately **not** gated on `isEditingEnabled`. That flag disables the
    // editor's own controls while a finish transaction runs; a mutation can
    // still arrive from somewhere else — a media import or a voice attach that
    // completes mid-promotion — and dropping it here is how the newer text
    // would vanish instead of being detected as a concurrent mutation.
    const next = change(this.#snapshot.entry);
    if (next === this.#snapshot.entry) return;
    this.#update({ entry: next });
    this.#touch();
  }

  /** F-JR-30: an attachment detached now, deleted only if the finish lands. */
  detachAttachment(id: JournalAttachmentId): void {
    this.mutate((entry) => ({
      ...entry,
      attachments: entry.attachments.filter((attachment) => attachment.id !== id),
    }));
    this.#update({
      pendingAttachmentDeletions: [...this.#snapshot.pendingAttachmentDeletions, id],
    });
  }

  /* --- autosave (F-JR-23) ------------------------------------------------ */

  /** Runs the pending write now. Safe to call with nothing pending. */
  async flush(): Promise<void> {
    this.#cancelDebounce?.();
    this.#cancelDebounce = undefined;
    await this.#runWriter();
  }

  /** F-JR-26's Retry: the same writer, without waiting out a debounce. */
  async retry(): Promise<void> {
    if (this.#snapshot.autosave.kind !== "failed") return;
    // The failed write left `savedGeneration` behind the current one, so the
    // writer has work to do the moment it is asked again — no state to reset.
    this.#update({ autosave: WAITING });
    await this.flush();
  }

  #touch(): void {
    this.#generation += 1;
    this.#update({ autosave: WAITING });
    this.#cancelDebounce?.();
    this.#cancelDebounce = this.#dependencies.ticker.after(
      JOURNAL_AUTOSAVE_DEBOUNCE,
      () => {
        this.#cancelDebounce = undefined;
        void this.#runWriter();
      },
    );
  }

  /**
   * One writer, shared. A second caller receives the promise of the first
   * rather than starting a race the storage layer would then have to order.
   */
  #runWriter(): Promise<void> {
    const existing = this.#writer;
    if (existing !== null) return existing;
    const promise = this.#write().finally(() => {
      this.#writer = null;
    });
    this.#writer = promise;
    return promise;
  }

  async #write(): Promise<void> {
    for (;;) {
      const generation = this.#generation;
      if (generation === this.#savedGeneration) return;

      const entry = this.#snapshot.entry;

      // F-JR-23: a blank new entry has nothing worth keeping. The draft is
      // discarded and the state resets to `untouched` — not to `saved`, because
      // nothing was.
      if (this.#origin === "new" && journalComposerIsBlank(entry)) {
        const discarded = await this.#dependencies.repository.discardDraft(entry.id);
        if (discarded.kind === "failed") {
          this.#fail(discarded.error);
          return;
        }
        this.#savedGeneration = generation;
        if (this.#generation === generation) {
          this.#update({ autosave: UNTOUCHED });
          this.#hasSameIdDraft = false;
          return;
        }
        continue;
      }

      this.#update({ autosave: SAVING });

      const draft: JournalDraft = {
        schemaVersion: JOURNAL_DRAFT_SCHEMA_VERSION,
        id: entry.id,
        entry,
        savedAt: this.#nextSavedAt(),
      };
      const result = await this.#dependencies.repository.saveDraft(draft);

      if (result.kind === "failed") {
        this.#fail(result.error);
        return;
      }

      this.#lastSavedAt = result.value.savedAt;
      this.#savedGeneration = generation;
      this.#hasSameIdDraft = true;

      // Only report `saved` when nothing advanced while the write was in
      // flight. Otherwise the loop runs again and the label stays honest about
      // there being newer text than the bytes on disk.
      if (this.#generation === generation) {
        this.#update({ autosave: { kind: "saved", at: result.value.savedAt } });
        return;
      }
    }
  }

  #nextSavedAt(): JournalInstant {
    const now = journalInstantFromMilliseconds(this.#dependencies.clock.now());
    return nextJournalSavedAt(now, this.#lastSavedAt);
  }

  #fail(error: JournalRepositoryErrorCode | undefined): void {
    const message = journalAutosaveFailureMessage(error);
    this.#update({ autosave: { kind: "failed", message } });
    // §8.5: assertive, because the user is still typing into something that is
    // no longer being saved and a polite announcement would wait for a gap that
    // may never come.
    this.#dependencies.announcer?.announce(journalAutosaveAnnouncement(message), {
      assertive: true,
    });
  }

  /* --- finish (F-JR-24) -------------------------------------------------- */

  /**
   * Flush, disable editing, promote, clean up.
   *
   * Read the ordering rather than the steps: editing is disabled **after** the
   * flush so the last keystroke is already durable before the transaction
   * begins, and the generation is captured **before** the promotion so a
   * keystroke that lands during it is detectable at all.
   */
  async finish(): Promise<JournalFinishOutcome> {
    if (this.#snapshot.isFinishing) {
      return { kind: "keptOpen", interruption: "alreadyFinishing" };
    }
    if (this.#dependencies.isMediaImportInProgress?.() === true) {
      return { kind: "keptOpen", interruption: "mediaImportInProgress" };
    }

    this.#update({ isFinishing: true });
    try {
      // A promotion that already succeeded retries cleanup only. Promoting the
      // same draft again would be a second revision step over a record that has
      // already taken one, which the repository refuses — permanently.
      const promoted = this.#promoted;
      if (promoted !== undefined) {
        if (!(await this.#cleanup())) {
          return { kind: "keptOpen", interruption: "cleanupFailed" };
        }
        this.#promoted = undefined;
        this.#adoptPromoted(promoted);
        return { kind: "saved", entry: promoted };
      }

      await this.flush();
      this.#update({ isEditingEnabled: false });

      const entry = this.#snapshot.entry;

      if (this.#origin === "new" && journalComposerIsBlank(entry)) {
        const discarded = await this.#dependencies.repository.discardDraft(entry.id);
        if (discarded.kind === "failed") {
          this.#reopen(discarded.error);
          return { kind: "keptOpen", interruption: "persistenceFailed" };
        }
        return { kind: "discardedBlank" };
      }

      // A persisted, untouched seed with no pending deletions short-circuits:
      // there is nothing to write, and writing anyway would burn a revision on
      // a record the user only looked at.
      const originEntry = this.#originEntry;
      if (
        this.#origin === "persisted" &&
        originEntry !== undefined &&
        !this.#hasSameIdDraft &&
        this.#snapshot.pendingAttachmentDeletions.length === 0 &&
        this.#dependencies.sameRecord(originEntry, {
          ...entry,
          revision: originEntry.revision,
        })
      ) {
        this.#update({ isEditingEnabled: true });
        return { kind: "saved", entry: originEntry };
      }

      const generation = this.#generation;
      const promotion = await this.#dependencies.repository.promoteDraft({
        schemaVersion: JOURNAL_DRAFT_SCHEMA_VERSION,
        id: entry.id,
        entry,
        savedAt: this.#nextSavedAt(),
      });

      if (promotion.kind === "failed") {
        this.#reopen(promotion.error);
        return { kind: "keptOpen", interruption: "persistenceFailed" };
      }

      if (this.#generation !== generation) {
        // A mutation landed during promotion. The durable entry is the one that
        // was promoted; the newer text is a *next* revision and is preserved as
        // a draft rather than thrown away or written over the record that just
        // landed.
        // The draft record is gone — `promoteDraft` removed it — so the next
        // write starts a fresh `savedAt` chain rather than one anchored to a
        // timestamp no stored draft carries any more.
        this.#lastSavedAt = null;
        this.#hasSameIdDraft = false;
        this.#update({
          entry: {
            ...this.#snapshot.entry,
            revision: nextJournalRevision(promotion.value.revision),
          },
          isEditingEnabled: true,
        });
        this.#touch();
        return { kind: "keptOpen", interruption: "concurrentMutation" };
      }

      if (!(await this.#cleanup())) {
        this.#promoted = promotion.value;
        return { kind: "keptOpen", interruption: "cleanupFailed" };
      }

      this.#adoptPromoted(promotion.value);
      return { kind: "saved", entry: promotion.value };
    } finally {
      this.#update({ isFinishing: false });
    }
  }

  /**
   * After a successful promotion the working revision becomes
   * `promoted.revision + 1`, so a composer that stays open — the summary's
   * recap flow reopens on the same entry — reserves the next step rather than
   * re-using the one just consumed.
   */
  #adoptPromoted(entry: JournalEntry): void {
    this.#hasSameIdDraft = false;
    this.#lastSavedAt = null;
    this.#savedGeneration = this.#generation;
    this.#update({
      entry: { ...entry, revision: nextJournalRevision(entry.revision) },
      isEditingEnabled: true,
      autosave: { kind: "saved", at: entry.updatedAt },
      pendingAttachmentDeletions: [],
    });
  }

  #reopen(error: JournalRepositoryErrorCode | undefined): void {
    this.#update({ isEditingEnabled: true });
    this.#fail(error);
  }

  async #cleanup(): Promise<boolean> {
    const pending = this.#snapshot.pendingAttachmentDeletions;
    if (pending.length === 0) return true;
    const remove = this.#dependencies.deleteAttachments;
    // No capability means nothing could have been detached, so an empty list is
    // the only reachable state — a non-empty one here is a composition bug, and
    // reporting failure keeps the entry and its media together.
    if (remove === undefined) return false;
    return remove(pending);
  }

  /* --- close and discard (F-JR-25) --------------------------------------- */

  closeDecision(): JournalCloseDecision {
    return journalCloseDecision({
      origin: this.#origin,
      isBlank: journalComposerIsBlank(this.#snapshot.entry),
      hasUnsavedChanges: journalHasUnsavedChanges(this.#snapshot.autosave),
      hasSameIdDraft: this.#hasSameIdDraft,
    });
  }

  /**
   * Close, preserving whatever the draft holds.
   *
   * Returns false when the flush failed: the composer stays open, because
   * closing over a failed save is closing over text that exists nowhere else.
   */
  async closePreservingDraft(): Promise<boolean> {
    await this.flush();
    return this.#snapshot.autosave.kind !== "failed";
  }

  /**
   * Discard: secure first, then remove.
   *
   * Both steps can fail independently, and each failure has its own sentence
   * because they mean different things — the first means nothing was removed
   * and the newest text may not be safe; the second means the draft is still
   * there and the editor did not close.
   */
  async discard(): Promise<JournalDiscardOutcome> {
    if (!(await this.closePreservingDraft())) {
      return { kind: "failed", message: JOURNAL_DISCARD_FAILURE.secureFailed };
    }
    const discarded = await this.#dependencies.repository.discardDraft(
      this.#snapshot.entry.id,
    );
    if (discarded.kind === "failed") {
      return { kind: "failed", message: JOURNAL_DISCARD_FAILURE.discardFailed };
    }
    this.#hasSameIdDraft = false;
    return { kind: "discarded" };
  }

  /** Cancels the pending debounce. Called when the composer unmounts. */
  dispose(): void {
    this.#cancelDebounce?.();
    this.#cancelDebounce = undefined;
  }

  /* --- internals --------------------------------------------------------- */

  /**
   * `hasUnsavedChanges` is derived here rather than passed in, so it cannot
   * disagree with the autosave state it is a function of — waiting, saving and
   * failed all mean there is text on screen that storage does not have.
   */
  #update(changes: Partial<JournalComposerSnapshot>): void {
    const next = { ...this.#snapshot, ...changes };
    this.#snapshot = {
      ...next,
      hasUnsavedChanges: journalHasUnsavedChanges(next.autosave),
    };
    for (const listener of this.#listeners) listener();
  }

  /** The seed the composer opened on. Read by the surface for its banners. */
  get seed(): JournalComposerSeed {
    return this.#seed;
  }
}
